/**
 * Builds the grouped-by-office meeting-room options used by the Details
 * form's Meeting Room select (Figma recipe — see
 * `CustomRenderOptionWithGroups[Multi]` in
 * `components/packages/core/src/components/molecules/Select/SelectVariations.stories.tsx`).
 *
 * - Each group corresponds to `room.office_name` (unknown rooms land in
 *   a generic "Other" bucket).
 * - Each option carries `data: { status, capacity }` so render props
 *   can paint the Available/Busy tag and the person-count footer.
 *
 * Fetching is done via the existing `useMeetingRooms` trigger + poll
 * hook. We refetch whenever the time window materially changes.
 */
import { useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { useMeetingRooms } from '@/api/useMeetingRooms';
import type { MeetingRoomGroup, MeetingRoomOptionData } from '@/formConfigs';

const OTHER_GROUP_LABEL = 'Other';

export interface UseMeetingRoomGroupedOptionsArgs {
  timezone: string;
  /** Preferred window — booked slot start/end in ISO. Falls back to
   *  today's 09:00–18:00 in `timezone` when no slot is picked yet. */
  slotStartIso?: string | null;
  slotEndIso?: string | null;
  /** Gate the fetch (e.g. only when the user has opened the room
   *  section). Defaults to true. */
  enabled?: boolean;
}

export interface UseMeetingRoomGroupedOptionsResult {
  groups: MeetingRoomGroup[];
  /** `email → displayName` map — lets the calendar swap the raw
   *  meeting subject for a friendly room label on busy events. */
  namesByEmail: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
}

export function useMeetingRoomGroupedOptions({
  timezone,
  slotStartIso,
  slotEndIso,
  enabled = true,
}: UseMeetingRoomGroupedOptionsArgs): UseMeetingRoomGroupedOptionsResult {
  const { rooms, isLoading, error, fetchRooms, cleanup } = useMeetingRooms();

  // Normalise the time window. Without a booked slot we ask for the
  // first reasonable future slot so rooms still show Available/Busy.
  const { startIso, endIso } = useMemo(() => {
    if (slotStartIso && slotEndIso) {
      return { startIso: slotStartIso, endIso: slotEndIso };
    }
    const start = moment().tz(timezone).startOf('day').add(9, 'hours');
    const end = start.clone().add(9, 'hours');
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }, [slotStartIso, slotEndIso, timezone]);

  useEffect(() => {
    if (!enabled) return;
    fetchRooms({ startTime: startIso, endTime: endIso, timezone });
    return () => {
      cleanup();
    };
    // cleanup ref is stable from the hook — exhaustive-deps noisy otherwise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, startIso, endIso, timezone]);

  const { groups, namesByEmail } = useMemo(() => {
    if (!rooms.length) {
      return {
        groups: [] as MeetingRoomGroup[],
        namesByEmail: {} as Record<string, string>,
      };
    }
    const byOffice = new Map<string, MeetingRoomGroup>();
    const names: Record<string, string> = {};
    for (const room of rooms) {
      if (!room.email) continue;
      names[room.email] = room.name;
      const officeName = room.office_name?.trim() || OTHER_GROUP_LABEL;
      let group = byOffice.get(officeName);
      if (!group) {
        group = { label: officeName, options: [] };
        byOffice.set(officeName, group);
      }
      const data: MeetingRoomOptionData = {
        status: room.available === false ? 'busy' : 'available',
        capacity: typeof room.capacity === 'number' ? room.capacity : undefined,
      };
      group.options.push({
        value: room.email,
        label: room.name,
        data,
      });
    }
    const sortedGroups = Array.from(byOffice.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return { groups: sortedGroups, namesByEmail: names };
  }, [rooms]);

  return { groups, namesByEmail, isLoading, error };
}
